import {Component, OnDestroy, OnInit} from '@angular/core';
import {ProjectBusinessLogicFilterService} from '../../shared/project-business-logic-filter/project-business-logic-filter.service';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {combineLatest, Observable, of, Subscription} from 'rxjs';
import {distinctUntilChanged, filter, map, switchMap, withLatestFrom} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {BusinessLogicListService} from '../../shared/project-business-logic-filter/business-logic-list.service';
import {AuthorizationService} from '../../authorization/authorization.service';
import {idAll} from '../../shared/project-business-logic-filter/project-business-logic-filter-utils';

@Component({
  selector: 'app-project-business-logic-filter',
  templateUrl: './project-business-logic-filter.component.html',
  styleUrls: ['./project-business-logic-filter.component.scss']
})
export class ProjectBusinessLogicFilterComponent implements OnInit, OnDestroy {

  formGroup: FormGroup;
  subscriptions: Subscription[] = [];
  projectList: Observable<any>;
  businessLogicList: Observable<any>;
  showProjects: Observable<boolean>;
  showBusinessLogics: Observable<boolean>;
  businessLogicGroupBy: Observable<string | null>;

  constructor(
    public projectBusinessLogicFilterService: ProjectBusinessLogicFilterService,
    private businessLogicListService: BusinessLogicListService,
    private translate: TranslateService,
    private authorizationService: AuthorizationService,
  ) {
    this.formGroup = new FormGroup({
      projectId: new FormControl(null, [Validators.required]),
      businessLogicId: new FormControl(null, [Validators.required]),
    });
  }

  ngOnInit() {
    this.projectBusinessLogicFilterService.onInit();
    this.projectList = this.projectBusinessLogicFilterService.projectList.pipe(
      map(
        l => l !== null ?
          [
            {id: idAll, custom_name: this.translate.instant('All projects')},
            ...l
          ]
          : [{id: idAll, custom_name: this.translate.instant('All projects')}]
      )
    );
    this.businessLogicGroupBy = this.projectBusinessLogicFilterService.selectedProjectId.pipe(
      map(
        selectedProjectId => selectedProjectId === idAll ? 'project_custom_name' : null
      )
    );
    this.subscriptions.push(
      this.projectList
        .pipe(
          filter(p => p !== null),
          // distinctUntilChanged(),
        )
        .subscribe(
          projects => {
            this.formGroup.controls.projectId.setValue(projects[0].id);
            // this.projectBusinessLogicFilterService.projectList.next(projects);
            // if (projects.length > 0 && !this.formGroup.controls.projectId.value) {
            //   const hasBusyfly = projects.some(p => p.id === this.busyFlyProjectId);
            //   this.formGroup.controls.projectId.setValue(hasBusyfly ? this.busyFlyProjectId : projects[0].id);
            // }
          }
        )
    );
    this.subscriptions.push(
      this.formGroup.controls.projectId.valueChanges.subscribe(
        (projectId) => {
          this.projectBusinessLogicFilterService.selectedProjectId.next(projectId);
        }
      )
    );
    this.subscriptions.push(
      this.formGroup.controls.businessLogicId.valueChanges
        .pipe(
          withLatestFrom(this.projectBusinessLogicFilterService.businessLogicList)
        )
        .subscribe(
        ([businessLogicId, businessLogics]) => {
          this.projectBusinessLogicFilterService.selectedBusinessLogicId.next(businessLogicId);
          if (businessLogics !== null && businessLogicId !== null) {
            if (businessLogicId === idAll) {
              this.projectBusinessLogicFilterService.selectedBusinessLogics.next(businessLogics);
            } else {
              const businessLogic = businessLogics.find(b => b.id === businessLogicId);
              this.projectBusinessLogicFilterService.selectedBusinessLogics.next([businessLogic]);
            }
          } else {
            this.projectBusinessLogicFilterService.selectedBusinessLogics.next([]);
          }
        }
      )
    );

    this.subscriptions.push(
      this.projectBusinessLogicFilterService.businessLogicList
        .subscribe(
          businessLogics => {
            if (businessLogics !== null && businessLogics.length > 0) {
              if (!this.formGroup.controls.businessLogicId.value) {
                this.formGroup.controls.businessLogicId.setValue(idAll);
                // const blIndex = businessLogics.findIndex(bl => bl.type === 'sharing');
                // this.formGroup.controls.businessLogicId.setValue(blIndex > -1 ? businessLogics[blIndex].id : businessLogics[0].id);
              }
            } else {
              this.formGroup.controls.businessLogicId.setValue(null);
            }
          }
        )
    );
    this.businessLogicList = this.projectBusinessLogicFilterService.businessLogicList.pipe(
      map(
        l => l !== null ?
          [
            {id: idAll, custom_name: this.translate.instant('All business models')},
            ...l
          ]
          : [{id: idAll, custom_name: this.translate.instant('All business models')}]
      )
    );
    this.showProjects =
      combineLatest(
        this.authorizationService.loggedInUserHasRightsNonLessThanAdminsManager(),
        this.projectBusinessLogicFilterService.projectList
      ).pipe(
        map(([loggedInUserHasRightsNonLessThanAdminsManager, projects]) => {
            return loggedInUserHasRightsNonLessThanAdminsManager && projects && projects.length > 1;
          }
        )
      );
    this.showBusinessLogics = this.projectBusinessLogicFilterService.businessLogicList.pipe(
      map((businessLogics) => businessLogics && businessLogics.length > 1
      )
    );
    this.observeBusinessLogicUpdates();
  }

  observeBusinessLogicUpdates() {
    this.subscriptions.push(
      this.projectBusinessLogicFilterService.triggerBusinessLogicUpdate.pipe(
        withLatestFrom(
          this.projectBusinessLogicFilterService.selectedProjectId,
          this.projectBusinessLogicFilterService.selectedBusinessLogicId
        ),
        switchMap(([, selectedProjectId, selectedBusinessLogicId]) => {
          const result = {
            selectedProjectId: selectedProjectId,
            selectedBusinessLogicId: selectedBusinessLogicId,
          };

          return selectedProjectId
            ? this.businessLogicListService.getBusinessLogics(selectedProjectId).pipe(
              map(businessLogicList => ({...result, businessLogicList: businessLogicList}))
            )
            : of({...result, businessLogicList: []});
        })
      ).subscribe(({selectedBusinessLogicId, businessLogicList}) => {
          this.projectBusinessLogicFilterService.refreshBusinessLogicList(businessLogicList);
          const businessLogic = businessLogicList.find(b => b.id === selectedBusinessLogicId);
          this.formGroup.controls.businessLogicId.setValue(
            businessLogic && businessLogic.id
              ? selectedBusinessLogicId :
              idAll);
        }
      )
    );
  }

  ngOnDestroy() {
    this.projectBusinessLogicFilterService.onDestroy();
    this.subscriptions.filter(s => !!s).forEach(s => s.unsubscribe());
  }
}
